import { policyEngineService } from '../../services/PolicyEngineService';
const state = {
    policies: [],
    evaluations: [],
    loading: false,
    error: null,
    dryRunResult: null,
};
const mutations = {
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_POLICIES(state, policies) {
        state.policies = policies;
    },
    SET_EVALUATIONS(state, evaluations) {
        state.evaluations = evaluations;
    },
    SET_DRY_RUN_RESULT(state, result) {
        state.dryRunResult = result;
    },
    ADD_POLICY(state, policy) {
        state.policies.push(policy);
        state.policies.sort((a, b) => a.priority - b.priority);
    },
    UPDATE_POLICY(state, updated) {
        const idx = state.policies.findIndex((p) => p.id === updated.id);
        if (idx !== -1)
            state.policies[idx] = updated;
        state.policies.sort((a, b) => a.priority - b.priority);
    },
    REMOVE_POLICY(state, id) {
        state.policies = state.policies.filter((p) => p.id !== id);
    },
};
const actions = {
    async fetchPolicies({ commit }, params) {
        try {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            const res = await policyEngineService.listPolicies(params);
            commit('SET_POLICIES', res.data.data ?? res.data);
        }
        catch (err) {
            commit('SET_ERROR', err.message || 'Failed to load policies');
        }
        finally {
            commit('SET_LOADING', false);
        }
    },
    async createPolicy({ commit }, data) {
        try {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            const res = await policyEngineService.createPolicy(data);
            commit('ADD_POLICY', res.data.data ?? res.data);
            return res.data.data ?? res.data;
        }
        catch (err) {
            commit('SET_ERROR', err.message || 'Failed to create policy');
            throw err;
        }
        finally {
            commit('SET_LOADING', false);
        }
    },
    async updatePolicy({ commit }, { id, data }) {
        try {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            const res = await policyEngineService.updatePolicy(id, data);
            commit('UPDATE_POLICY', res.data.data ?? res.data);
            return res.data.data ?? res.data;
        }
        catch (err) {
            commit('SET_ERROR', err.message || 'Failed to update policy');
            throw err;
        }
        finally {
            commit('SET_LOADING', false);
        }
    },
    async deletePolicy({ commit }, id) {
        try {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            await policyEngineService.deletePolicy(id);
            commit('REMOVE_POLICY', id);
        }
        catch (err) {
            commit('SET_ERROR', err.message || 'Failed to delete policy');
            throw err;
        }
        finally {
            commit('SET_LOADING', false);
        }
    },
    async runDryEvaluate({ commit }, context) {
        try {
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            commit('SET_DRY_RUN_RESULT', null);
            const res = await policyEngineService.dryRunEvaluate(context);
            const result = res.data.data ?? res.data;
            commit('SET_DRY_RUN_RESULT', result);
            return result;
        }
        catch (err) {
            commit('SET_ERROR', err.message || 'Dry-run failed');
            throw err;
        }
        finally {
            commit('SET_LOADING', false);
        }
    },
    async fetchEvaluations({ commit }, params) {
        try {
            commit('SET_ERROR', null);
            const res = await policyEngineService.listEvaluations(params);
            commit('SET_EVALUATIONS', res.data.data ?? res.data);
        }
        catch (err) {
            commit('SET_ERROR', err.message || 'Failed to load evaluations');
        }
    },
};
const getters = {
    policies: (state) => state.policies,
    evaluations: (state) => state.evaluations,
    loading: (state) => state.loading,
    error: (state) => state.error,
    dryRunResult: (state) => state.dryRunResult,
    policiesByType: (state) => {
        return state.policies.reduce((acc, p) => {
            if (!acc[p.policy_type])
                acc[p.policy_type] = [];
            acc[p.policy_type].push(p);
            return acc;
        }, {});
    },
    enabledPolicies: (state) => state.policies.filter((p) => p.enabled),
};
const policyEngine = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
export default policyEngine;
